import { useDepsChanged } from './chunk-I37XHMWY.js';
import { notifyError } from './chunk-3WCKEFHJ.js';
import * as React4 from 'react';
import { jsx } from 'react/jsx-runtime';
import { thrownToErrorEnvelope, errorEnvelopeToErrorBody } from '@authe/fe-commons/api';
import { applyServerErrors } from '@authe/fe-commons/forms';

var ApiClientContext = React4.createContext(null);
function ApiProvider({ client, children }) {
  return /* @__PURE__ */ jsx(ApiClientContext.Provider, { value: client, children });
}
function useApiClient() {
  const client = React4.useContext(ApiClientContext);
  if (!client) throw new Error("useApiClient must be used inside <ApiProvider>");
  return client;
}
function useOptionalApiClient() {
  return React4.useContext(ApiClientContext);
}
var Context = React4.createContext(null);
var useOptionalResourceChanges = () => React4.useContext(Context);
function ResourceChangesProvider({ hub, children, revalidateOnFocus = true, revalidateOnOnline = true }) {
  React4.useEffect(() => {
    if (!hub || typeof window === "undefined") return;
    let last = -Infinity;
    const refresh = () => {
      if (document.visibilityState === "hidden" || Date.now() - last < 1e3) return;
      last = Date.now();
      hub.revalidate();
    };
    if (revalidateOnFocus) {
      window.addEventListener("focus", refresh);
      document.addEventListener("visibilitychange", refresh);
    }
    if (revalidateOnOnline) window.addEventListener("online", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [hub, revalidateOnFocus, revalidateOnOnline]);
  return /* @__PURE__ */ jsx(Context.Provider, { value: hub, children });
}
function useResourceInvalidation(resources, refresh) {
  const hub = useOptionalResourceChanges();
  const resourceKey = JSON.stringify(typeof resources === "string" ? [resources] : resources);
  const latest = React4.useRef(refresh);
  React4.useEffect(() => {
    latest.current = refresh;
  });
  React4.useEffect(() => {
    const subscribed = JSON.parse(resourceKey);
    if (!hub || !subscribed.length) return;
    return hub.subscribeBatched((changed) => {
      if (subscribed.some((resource) => changed.has(resource))) latest.current();
    });
  }, [hub, resourceKey]);
}
function useResourceRevision(resources) {
  const [revision, setRevision] = React4.useState(0);
  useResourceInvalidation(resources, () => setRevision((value) => value + 1));
  return revision;
}
function queryData(data) {
  return { statusCode: 200, ok: true, data };
}
function emptyState(enabled) {
  return {
    data: void 0,
    envelope: null,
    error: null,
    isLoading: enabled,
    isFetching: false,
    isPlaceholderData: false
  };
}
function useQuery(fetcher, options = {}) {
  const client = useApiClient();
  const enabled = options.enabled !== false;
  const keepPreviousData = options.keepPreviousData === true;
  const fetcherRef = React4.useRef(fetcher);
  fetcherRef.current = fetcher;
  const onSuccessRef = React4.useRef(options.onSuccess);
  onSuccessRef.current = options.onSuccess;
  const onErrorRef = React4.useRef(options.onError);
  onErrorRef.current = options.onError;
  const restored = () => {
    const hit = enabled ? options.cachedResponse?.(client) : void 0;
    return hit?.ok ? { data: hit.data, envelope: hit, error: null, isLoading: false, isFetching: false, isPlaceholderData: false } : emptyState(enabled);
  };
  const cachedRef = React4.useRef(options.cachedResponse);
  cachedRef.current = options.cachedResponse;
  const lastTick = React4.useRef(0);
  const [state, setState] = React4.useState(restored);
  const [tick, setTick] = React4.useState(0);
  const refetch = React4.useCallback(() => setTick((t) => t + 1), []);
  useResourceInvalidation(enabled ? options.resourcePolicy?.resources ?? [] : [], refetch);
  const extraDeps = options.deps ?? [];
  const priorClient = React4.useRef(client);
  const clientChanged = priorClient.current !== client;
  priorClient.current = client;
  if (useDepsChanged([client, ...extraDeps])) {
    setState(
      (s) => !clientChanged && keepPreviousData && s.data !== void 0 ? { ...s, error: null, isLoading: false, isFetching: enabled, isPlaceholderData: true } : restored()
    );
  }
  React4.useEffect(() => {
    if (!enabled) return;
    const manual = tick !== lastTick.current;
    lastTick.current = tick;
    const cached = manual ? void 0 : cachedRef.current?.(client);
    if (cached?.ok) {
      setState({ data: cached.data, envelope: cached, error: null, isLoading: false, isFetching: false, isPlaceholderData: false });
      return;
    }
    let active = true;
    const ctrl = new AbortController();
    setState((s) => ({ ...s, isFetching: true, isLoading: s.data === void 0, error: null }));
    fetcherRef.current(client, ctrl.signal).then(
      (res) => {
        if (!active) return;
        if (res.ok) {
          setState({
            data: res.data,
            envelope: res,
            error: null,
            isLoading: false,
            isFetching: false,
            isPlaceholderData: false
          });
          onSuccessRef.current?.(res.data, res);
        } else {
          setState((s) => ({ ...s, error: res, isLoading: false, isFetching: false }));
          onErrorRef.current?.(res);
        }
      },
      (err) => {
        if (!active) return;
        if (ctrl.signal.aborted) {
          setState((s) => ({ ...s, isFetching: false }));
          return;
        }
        const envelope = thrownToErrorEnvelope(err);
        setState((s) => ({ ...s, error: envelope, isLoading: false, isFetching: false }));
        onErrorRef.current?.(envelope);
      }
    );
    return () => {
      active = false;
      ctrl.abort();
    };
  }, [client, enabled, tick, ...extraDeps]);
  return { ...state, refetch };
}
function useAsyncResource(loader, deps) {
  const [data, setCachedData] = React4.useState();
  const [isLoading, setIsLoading] = React4.useState(true);
  const [error, setError] = React4.useState(null);
  const [tick, setTick] = React4.useState(0);
  const revision = React4.useRef(0);
  const setData = React4.useCallback((next) => {
    revision.current += 1;
    setCachedData(next);
    setError(null);
    setIsLoading(false);
  }, []);
  const loaderRef = React4.useRef(loader);
  React4.useLayoutEffect(() => {
    loaderRef.current = loader;
  });
  React4.useLayoutEffect(() => {
    revision.current += 1;
  }, deps);
  if (useDepsChanged(deps)) {
    setCachedData(void 0);
    setError(null);
    setIsLoading(true);
  }
  React4.useEffect(() => {
    let alive = true;
    const startedAt = revision.current;
    setIsLoading(true);
    setError(null);
    loaderRef.current().then((result) => {
      if (alive && startedAt === revision.current) {
        setCachedData(result);
        setIsLoading(false);
      }
    }).catch((err) => {
      if (alive && startedAt === revision.current) {
        setError(err);
        setIsLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, [...deps, tick]);
  const refetch = React4.useCallback(() => setTick((value) => value + 1), []);
  return { data, isLoading, error, refetch, setData };
}
function useMutation(fn, options = {}) {
  const client = useApiClient();
  const optionsRef = React4.useRef(options);
  optionsRef.current = options;
  const [data, setData] = React4.useState(void 0);
  const [error, setError] = React4.useState(null);
  const [isLoading, setIsLoading] = React4.useState(false);
  const reset = React4.useCallback(() => {
    setData(void 0);
    setError(null);
    setIsLoading(false);
  }, []);
  const mutate = React4.useCallback(
    async (args) => {
      const opts = optionsRef.current;
      setIsLoading(true);
      setError(null);
      let res;
      try {
        res = await fn(client, args);
      } catch (err) {
        res = thrownToErrorEnvelope(err);
      }
      if (res.ok) {
        setData(res.data);
        setError(null);
        opts.onSuccess?.(res.data, res, args);
      } else {
        setError(res);
        opts.onError?.(res, args);
        if (opts.setFormError) applyServerErrors(errorEnvelopeToErrorBody(res), opts.setFormError);
        if (opts.notifyOnError) {
          notifyError(res, typeof opts.notifyOnError === "object" ? opts.notifyOnError : {});
        }
      }
      setIsLoading(false);
      return res;
    },
    [client, fn]
  );
  return { mutate, data, error, isLoading, reset };
}

export { ApiProvider, ResourceChangesProvider, queryData, useApiClient, useAsyncResource, useMutation, useOptionalApiClient, useOptionalResourceChanges, useQuery, useResourceInvalidation, useResourceRevision };
//# sourceMappingURL=chunk-GWVFEJYV.js.map
//# sourceMappingURL=chunk-GWVFEJYV.js.map